import type { CustomerDetailState, CustomersListState } from "../../../components/authenticated/CustomersWorkspace";
import type { OnboardingSetupSnapshot } from "../../onboarding";
import type { BookingPolicySnapshot } from "../../settings";
import type { AgendaState, AgendaWindow, BookingPolicyState, SetupState, ShellState } from "./shellTypes";

export function toShellError(error: unknown): ShellState {
  if (isApiError(error)) {
    if (error.code === "auth_required") {
      return { status: "error", title: "Sessao invalida", message: "A sessao informada nao autorizou o boot do app.", retryable: false };
    }

    if (error.code === "invalid_response") {
      return { status: "error", title: "Contexto inconsistente", message: "Nao foi possivel confirmar a sessao com seguranca.", retryable: false };
    }

    return { status: "error", title: "Falha no boot", message: "Nao foi possivel carregar o app autenticado agora.", retryable: error.retryable };
  }

  return { status: "error", title: "Falha inesperada", message: "Nao foi possivel concluir o boot autenticado.", retryable: true };
}

export function toAgendaError(error: unknown, window: AgendaWindow): AgendaState {
  if (isApiError(error)) {
    if (error.code === "auth_required") {
      return { status: "error", title: "Sessao expirada", message: "Entre novamente para carregar a agenda.", retryable: false, window };
    }

    if (error.code === "invalid_response") {
      return { status: "error", title: "Contrato da agenda mudou", message: "A agenda retornou em um formato inesperado.", retryable: false, window };
    }

    if (error.code === "http_error" && error.status === 400) {
      return { status: "error", title: "Janela da agenda invalida", message: "A agenda do dia nao pode ser carregada com esse intervalo.", retryable: false, window };
    }

    return { status: "error", title: "Falha ao carregar agenda", message: "Nao foi possivel buscar os agendamentos agora.", retryable: error.retryable, window };
  }

  return { status: "error", title: "Falha inesperada", message: "Nao foi possivel carregar a agenda do dia.", retryable: true, window };
}

export function toCustomersListError(error: unknown): CustomersListState {
  if (isApiError(error)) {
    if (error.code === "auth_required") {
      return { status: "error", title: "Sessao expirada", message: "Entre novamente para carregar clientes.", retryable: false };
    }

    if (error.code === "http_error" && error.status === 403) {
      return { status: "error", title: "Acesso indisponivel", message: "Esta sessao nao tem permissao para consultar clientes.", retryable: false };
    }

    return { status: "error", title: "Falha ao carregar clientes", message: "Nao foi possivel buscar a lista de clientes agora.", retryable: error.retryable };
  }

  return { status: "error", title: "Falha inesperada", message: "Nao foi possivel carregar clientes.", retryable: true };
}

export function toCustomerDetailError(error: unknown, customerID: string): CustomerDetailState {
  if (isApiError(error)) {
    if (error.code === "auth_required") {
      return { status: "error", title: "Sessao expirada", message: "Entre novamente para abrir o detalhe do cliente.", retryable: false, customerID };
    }

    if (error.code === "http_error" && error.status === 404) {
      return { status: "error", title: "Cliente indisponivel", message: "O cliente nao foi encontrado para esta sessao.", retryable: false, customerID };
    }

    if (error.code === "http_error" && error.status === 403) {
      return { status: "error", title: "Acesso indisponivel", message: "Esta sessao nao tem permissao para consultar este cliente.", retryable: false, customerID };
    }

    return { status: "error", title: "Falha ao carregar detalhe", message: "Nao foi possivel buscar dados, observacoes e historico do cliente.", retryable: error.retryable, customerID };
  }

  return { status: "error", title: "Falha inesperada", message: "Nao foi possivel carregar o detalhe do cliente.", retryable: true, customerID };
}

export function toBookingPolicyError(error: unknown, policy?: BookingPolicySnapshot): BookingPolicyState {
  if (isApiError(error)) {
    if (error.code === "auth_required") {
      return { status: "error", title: "Sessao expirada", message: "Entre novamente para carregar configuracoes.", retryable: false, policy };
    }

    if (error.code === "http_error" && error.status === 403) {
      return { status: "error", title: "Acesso indisponivel", message: "Esta sessao nao tem permissao para alterar politicas.", retryable: false, policy };
    }

    if (error.code === "http_error" && error.status === 400) {
      return { status: "error", title: "Revise as configuracoes", message: "Algum campo nao passou na validacao final.", retryable: false, policy };
    }

    if (error.code === "invalid_response") {
      return { status: "error", title: "Politica indisponivel", message: "A politica de agendamento retornou em formato inesperado.", retryable: false, policy };
    }

    return { status: "error", title: "Falha ao carregar configuracoes", message: "Nao foi possivel sincronizar a politica de agendamento agora.", retryable: error.retryable, policy };
  }

  return { status: "error", title: "Falha inesperada", message: "Nao foi possivel concluir a operacao de configuracoes.", retryable: true, policy };
}

export function toSetupError(error: unknown, setup?: OnboardingSetupSnapshot): SetupState {
  if (isApiError(error)) {
    if (error.code === "auth_required") {
      return { status: "error", title: "Sessao expirada", message: "Entre novamente para continuar o setup.", retryable: false, setup };
    }

    if (error.code === "http_error" && error.status === 400) {
      return { status: "error", title: "Revise os dados", message: "Algum campo nao passou na validacao final.", retryable: false, setup };
    }

    return { status: "error", title: "Falha no setup", message: "Nao foi possivel atualizar o onboarding agora.", retryable: error.retryable, setup };
  }

  return { status: "error", title: "Falha inesperada", message: "Nao foi possivel concluir o setup inicial.", retryable: true, setup };
}

function isApiError(error: unknown): error is { code: string; status?: number; retryable: boolean } {
  return typeof error === "object"
    && error !== null
    && "code" in error
    && "retryable" in error;
}
